import { ActionIcon } from '@mantine/core';
import { memo, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { toastNotifications } from 'shared/utils/toastNotifications';

interface ConnectorPlacementButtonProps {
    isAutoConnectorEnabledRef: RefObject<boolean>;
    placementButtonRef: RefObject<HTMLButtonElement | null>;
    articleDataLoaded: boolean;
    isLoading: boolean;
    handleConnectorPlacementStart: () => void;
    updateActionIcon: () => void;
}

export const ConnectorPlacementButton = memo(
    ({
        isAutoConnectorEnabledRef,
        placementButtonRef,
        articleDataLoaded,
        isLoading,
        handleConnectorPlacementStart,
        updateActionIcon
    }: ConnectorPlacementButtonProps) => {
        const { t } = useTranslation();

        const handlePlacementButtonRef = (actionIcon: HTMLButtonElement | null) => {
            if (placementButtonRef.current === null && actionIcon) {
                placementButtonRef.current = actionIcon;
            }

            updateActionIcon();
        };

        const handleOnPointerDown = () => {
            if (isLoading) {
                return;
            }

            if (isAutoConnectorEnabledRef.current) {
                handleConnectorPlacementStart();
            } else {
                toastNotifications.information({
                    title: t('info'),
                    message: articleDataLoaded ? t('infoAutoConnectorPlacement') : t('labelPlaceConnectorsDisabled')
                });
            }
        };

        return (
            <ActionIcon
                ref={handlePlacementButtonRef}
                aria-busy={isLoading}
                className="pointer-events-auto action-icon-medium automated-placement-action-icon max-[1440px]:h-7.5 max-[1440px]:w-7.5 max-[1440px]:text-icon-sm max-[850px]:h-7.5 max-[850px]:w-7.5 max-[850px]:text-icon-sm"
                disabled={isLoading}
                variant="activated"
                onPointerDown={handleOnPointerDown}
            >
                {isLoading ? 'hourglass_top' : 'auto_fix'}
            </ActionIcon>
        );
    }
);

ConnectorPlacementButton.displayName = 'ConnectorPlacementButton';
